import { useState } from 'react';
import {
  canApprove221,
  historyIsComplete221,
  packageStatusLabels221,
  primaryRewardCount221,
  rollbackTarget221,
  unacknowledged221,
  warningLabels221,
  type PackageVersion221,
  type PackageWarning221,
  type WarningCode221,
} from '../domain/contentFactory221';
import { AppFrame, Card, Metric, PrimaryButton, ProgressBar, SecondaryButton, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';

const demoPackage = {
  id: 'pkg_2026-08-01',
  contentDate: '2026-08-01',
  title: '장바구니 물가가 오른 이유',
  sourceName: '경제일보',
  sourcePublishedAt: '2026-07-31',
  completion: 100,
  rewardPolicy: { points: 23, iceBricks: 1, puzzlePieces: 0, specialBrickType: 'none' },
  bundle: ['대표 기사', '난이도 3종', '퀴즈', '생활 미션', '가족 미션', '보상', '퍼즐 조각', '스노스 포즈'],
};

const demoWarnings: PackageWarning221[] = [
  { code: 'missingSource', message: '출처 링크가 비어 있습니다.' },
  { code: 'rewardHeavy', message: '포인트 지급량이 평소보다 많습니다.' },
];

const baseVersions: PackageVersion221[] = [
  { version: 1, status: 'published', approvedBy: '운영자', approvedAt: '2026-07-25', note: '초기 게시' },
  { version: 2, status: 'approved', approvedBy: '운영자', approvedAt: '2026-07-31', note: '2차 검수' },
  { version: 3, status: 'republished', approvedBy: '운영자', approvedAt: '2026-08-01', note: '게시' },
];

type Stage = 'bundle' | 'warnings' | 'publish' | 'rollback';
const stages: Stage[] = ['bundle', 'warnings', 'publish', 'rollback'];
const stageLabels: Record<Stage, string> = {
  bundle: '콘텐츠 패키지',
  warnings: '경고 확인',
  publish: '한 번의 승인',
  rollback: '롤백',
};

export function ContentFactoryDemoScreen221() {
  const [stageIndex, setStageIndex] = useState(0);
  const [acknowledged, setAcknowledged] = useState<WarningCode221[]>([]);
  const [versions, setVersions] = useState<PackageVersion221[]>(baseVersions);
  const stage = stages[stageIndex];

  const liveVersion = Math.max(...versions.map((item) => item.version));
  const rewardCount = primaryRewardCount221(demoPackage.rewardPolicy);
  const pending = unacknowledged221(demoWarnings, acknowledged);
  const approvable = canApprove221(demoWarnings, acknowledged, demoPackage.completion, rewardCount);
  const target = rollbackTarget221(versions, liveVersion);

  const toggle = (code: WarningCode221) =>
    setAcknowledged((current) => (current.includes(code) ? current.filter((item) => item !== code) : [...current, code]));

  const rollback = () => {
    if (!target) return;
    setVersions((current) => [...current, {
      version: Math.max(...current.map((item) => item.version)) + 1,
      status: 'republished', approvedBy: '운영자', approvedAt: '2026-08-01',
      note: `버전 ${target.version} 롤백`,
    }]);
  };

  const reset = () => { setStageIndex(0); setAcknowledged([]); setVersions(baseVersions); };

  return <AppFrame title="MRM 2.21 콘텐츠 팩토리 데모" backTo="/demo" screenClass="content-factory-screen-2-21">
    <div className="demo-sample-banner">체험용 고정 운영 데이터 · 실제 계정과 완전 분리</div>
    <StatusBanner />

    <section className="content-factory-hero-2-21">
      <GuideCharacter guide="snows" pose="research" state="guide" alt="콘텐츠 운영을 안내하는 스노스" priority/>
      <div>
        <p className="eyebrow">Content Factory 2.0</p>
        <h1>{stageLabels[stage]}</h1>
        <p>하루치 콘텐츠를 하나의 배포 단위로 묶고, 승인할 때마다 되돌릴 수 있는 버전을 남깁니다.</p>
      </div>
    </section>

    <div className="content-factory-demo-steps-2-21">
      {stages.map((item, index) => (
        <button type="button" key={item} className={index === stageIndex ? 'active' : ''} onClick={() => setStageIndex(index)}>
          {index + 1}<span>{stageLabels[item]}</span>
        </button>
      ))}
    </div>

    <div className="content-factory-demo-grid-2-21">
      {stage === 'bundle' && <Card>
        <div className="card-heading"><div><span>{demoPackage.contentDate}</span><h2>{demoPackage.title}</h2></div><strong>{demoPackage.completion}%</strong></div>
        <ProgressBar value={demoPackage.completion}/>
        <p className="content-factory-source-2-21">출처 <b>{demoPackage.sourceName}</b> · 기준일 <b>{demoPackage.sourcePublishedAt}</b></p>
        <div className="content-factory-bundle-2-21">
          {demoPackage.bundle.map((item) => <span key={item}>{item}</span>)}
        </div>
        <p className="content-factory-note-2-21">이 항목들이 하나의 배포 단위입니다. 따로 게시하지 않습니다.</p>
      </Card>}

      {stage === 'warnings' && <Card>
        <div className="card-heading"><div><span>자동 점검</span><h2>경고는 막지 않고 확인을 남깁니다</h2></div><strong>{pending.length}건 미확인</strong></div>
        <div className="content-factory-warning-list-2-21">
          {demoWarnings.map((warning) => (
            <label key={warning.code} className={acknowledged.includes(warning.code) ? 'checked' : ''}>
              <input type="checkbox" checked={acknowledged.includes(warning.code)} onChange={() => toggle(warning.code)}/>
              <div><b>{warningLabels221[warning.code]}</b><small>{warning.message}</small></div>
            </label>
          ))}
        </div>
        <p className={approvable ? 'content-factory-ok-2-21' : 'content-factory-blocked-2-21'}>
          {approvable ? '모든 경고를 확인했습니다. 승인할 수 있습니다.' : '확인하지 않은 경고가 있어 승인할 수 없습니다.'}
        </p>
        <p className="content-factory-note-2-21">자동화는 초안과 경고까지만 담당하고, 최종 게시는 사람이 승인합니다.</p>
      </Card>}

      {stage === 'publish' && <Card>
        <div className="card-heading"><div><span>동시 배포</span><h2>기사·퀴즈·미션·보상이 함께 나갑니다</h2></div><strong>v{liveVersion}</strong></div>
        <div className="content-factory-metric-row-2-21">
          <Metric label="포인트" value={`${demoPackage.rewardPolicy.points}P`}/>
          <Metric label="얼음 벽돌" value={`${demoPackage.rewardPolicy.iceBricks}개`}/>
          <Metric label="주요 보상" value={`${rewardCount}종`}/>
        </div>
        <div className="content-factory-bundle-2-21">
          <span>경제기사</span><span>퀴즈</span><span>생활 미션</span><span>가족 미션</span><span>보상</span><span>공통 문항은행</span>
        </div>
        <p className="content-factory-note-2-21">코드 수정이나 재배포 없이 승인 한 번으로 반영됩니다.</p>
      </Card>}

      {stage === 'rollback' && <Card>
        <div className="card-heading"><div><span>버전 이력</span><h2>되돌려도 기록은 사라지지 않습니다</h2></div><strong>{versions.length}개 버전</strong></div>
        <div className="content-factory-version-list-2-21">
          {[...versions].sort((a, b) => b.version - a.version).map((item) => (
            <div key={item.version} className={item.version === liveVersion ? 'live' : ''}>
              <b>v{item.version}</b>
              <span>{packageStatusLabels221[item.status]} · {item.note}</span>
              <small>{item.approvedAt}</small>
            </div>
          ))}
        </div>
        <p className="content-factory-note-2-21">
          {target ? `롤백하면 버전 ${target.version}의 내용이 새 버전으로 다시 게시됩니다.` : '되돌릴 이전 게시 버전이 없습니다.'}
          {' '}이력 연속성 {historyIsComplete221(versions) ? '정상' : '손상'}.
        </p>
        <SecondaryButton onClick={rollback}>직전 게시 버전으로 롤백</SecondaryButton>
      </Card>}
    </div>

    <div className="demo-213-actions">
      <SecondaryButton onClick={reset}>초기화</SecondaryButton>
      <PrimaryButton onClick={() => setStageIndex((stageIndex + 1) % stages.length)}>다음 단계</PrimaryButton>
    </div>
  </AppFrame>;
}
